import {
  Bell,
  Calendar,
  Check,
  CheckCircle2,
  Clock,
  ExternalLink,
  FileText,
  ShieldCheck,
  Wallet,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { relativeTime } from "@/pages/admin/data";
import { adminService, retailerService, walletService } from "@/services/apiServices";
import { getSelfRetailerId } from "@/components/feature/auth/session";

interface LiveNotification {
  id: string;
  title: string;
  description: string;
  timestamp: string;
  category: "mandate" | "wallet" | "kyc" | "installment" | "system";
  link?: string;
}

interface NotificationBellProps {
  adminBadge?: boolean;
}

const STORAGE_KEY = "mandatepro_read_notifications";

export default function NotificationBell({ adminBadge }: NotificationBellProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<LiveNotification[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const containerRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const items: LiveNotification[] = [];

      if (adminBadge) {
        // 1. Fetch live audit logs and wallet pending summary from database
        const [logsRes, walletRes] = await Promise.allSettled([
          adminService.getLogs(),
          walletService.getAdminSummary(),
        ]);

        if (walletRes.status === "fulfilled") {
          const pendingTopupsCount = Number(walletRes.value?.data?.stats?.pendingCount || 0);
          if (pendingTopupsCount > 0) {
            items.push({
              id: `notif-wallet-pending-${pendingTopupsCount}`,
              title: "Wallet Top-up Request",
              description: `${pendingTopupsCount} wallet top-up request${pendingTopupsCount > 1 ? "s" : ""} awaiting approval`,
              timestamp: new Date().toISOString(),
              category: "wallet",
              link: "/admin/wallets",
            });
          }
        }

        // Real database audit logs (excluding KYC)
        if (logsRes.status === "fulfilled" && Array.isArray(logsRes.value?.data?.logs)) {
          const dbLogs = logsRes.value.data.logs.slice(0, 20);
          for (const l of dbLogs) {
            const eventName = String(l.event || "").toLowerCase();
            const desc = String(l.description || "").toLowerCase();

            // Ignore any KYC-related activities
            if (eventName.includes("kyc") || desc.includes("kyc")) {
              continue;
            }

            let cat: "mandate" | "wallet" | "kyc" | "installment" | "system" = "system";
            if (eventName.includes("mandate") || desc.includes("mandate")) {
              cat = "mandate";
            } else if (eventName.includes("wallet") || desc.includes("wallet") || desc.includes("topup")) {
              cat = "wallet";
            } else if (eventName.includes("installment") || desc.includes("installment")) {
              cat = "installment";
            }

            let link: string | undefined = "/admin/logs";
            if (l.refNo && l.refNo !== "—") {
              link = `/admin/mandates/${l.refNo}`;
            } else if (cat === "wallet") {
              link = "/admin/wallets";
            } else if (cat === "installment") {
              link = "/admin/installments";
            }

            items.push({
              id: String(l.id || `log-${l.timestamp}`),
              title: l.event || "System Activity",
              description: l.description || (l.refNo ? `Ref: ${l.refNo}` : "Database event recorded"),
              timestamp: l.timestamp || new Date().toISOString(),
              category: cat,
              link,
            });
          }
        }
      } else {
        // Retailer live alerts from database & operations
        try {
          const rawUser = localStorage.getItem("mandatepro_user");
          const user = rawUser ? JSON.parse(rawUser) : null;
          const rid = user?.id || getSelfRetailerId();
          if (rid) {
            const res = await retailerService.getNotifications(rid);
            if (res?.data?.notifications && Array.isArray(res.data.notifications)) {
              // Strictly exclude any notification that is KYC or references KYC
              const nonKyc = res.data.notifications.filter(
                (n: any) =>
                  n.category !== "kyc" &&
                  !String(n.title || "").toLowerCase().includes("kyc") &&
                  !String(n.description || "").toLowerCase().includes("kyc")
              );
              items.push(...nonKyc);
            }
          }
        } catch (retailerNotifErr) {
          console.warn("Retailer notification fetch error:", retailerNotifErr);
        }
      }

      setNotifications(items);
    } catch (err) {
      console.warn("Notification fetch notice:", err);
    }
  }, [adminBadge]);

  useEffect(() => {
    fetchNotifications();

    const handleUpdate = () => fetchNotifications();
    window.addEventListener("mandatepro_wallet_badge_updated", handleUpdate);
    window.addEventListener("focus", handleUpdate);

    const interval = setInterval(fetchNotifications, 30000);

    return () => {
      window.removeEventListener("mandatepro_wallet_badge_updated", handleUpdate);
      window.removeEventListener("focus", handleUpdate);
      clearInterval(interval);
    };
  }, [fetchNotifications]);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter so already seen/read notifications are removed from view, and ensure NO KYC notifications are shown
  const visibleNotifications = useMemo(() => {
    return notifications.filter((n) => {
      if (readIds.has(n.id)) return false;
      if (n.category === "kyc") return false;
      const t = (n.title || "").toLowerCase();
      const d = (n.description || "").toLowerCase();
      if (t.includes("kyc") || d.includes("kyc")) return false;
      return true;
    });
  }, [notifications, readIds]);

  const unreadCount = visibleNotifications.length;

  const markAllAsRead = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const nextSet = new Set(readIds);
    notifications.forEach((n) => nextSet.add(n.id));
    setReadIds(nextSet);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(nextSet)));
    } catch {
      // ignore
    }
  };

  const handleNotificationClick = (item: LiveNotification) => {
    const nextSet = new Set(readIds);
    nextSet.add(item.id);
    setReadIds(nextSet);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(nextSet)));
    } catch {
      // ignore
    }

    setIsOpen(false);
    if (item.link) {
      navigate(item.link);
    }
  };

  const handleDismissOne = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const nextSet = new Set(readIds);
    nextSet.add(id);
    setReadIds(nextSet);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(nextSet)));
    } catch {
      // ignore
    }
  };

  const getCategoryIcon = (category: LiveNotification["category"]) => {
    switch (category) {
      case "mandate":
        return (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <FileText className="h-4 w-4" />
          </div>
        );
      case "wallet":
        return (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-50 text-purple-600">
            <Wallet className="h-4 w-4" />
          </div>
        );
      case "kyc":
        return (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <ShieldCheck className="h-4 w-4" />
          </div>
        );
      case "installment":
        return (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
            <Calendar className="h-4 w-4" />
          </div>
        );
      default:
        return (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
            <CheckCircle2 className="h-4 w-4" />
          </div>
        );
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setIsOpen((v) => !v);
          if (!isOpen) fetchNotifications();
        }}
        className="relative flex h-9 w-9 items-center justify-center rounded-md text-ink-secondary hover:bg-slate-100 cursor-pointer transition-colors"
        aria-label="Notifications"
        title={unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}` : "Notifications"}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white ring-2 ring-white animate-pulse">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Floating Notifications Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 rounded-xl border border-line bg-white shadow-dropdown z-50 overflow-hidden animate-fadeIn">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-line px-4 py-3 bg-slate-50/50">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-ink">Notifications</h3>
              {unreadCount > 0 && (
                <span className="rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-600">
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllAsRead}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer transition-colors"
              >
                <Check className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>

          {/* List of active unread/unseen notifications */}
          <div className="max-h-[380px] overflow-y-auto divide-y divide-slate-100">
            {visibleNotifications.length === 0 ? (
              <div className="py-10 text-center px-4">
                <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto opacity-70" />
                <p className="mt-2 text-sm font-medium text-ink">All caught up!</p>
                <p className="text-xs text-ink-secondary mt-0.5">No new notifications</p>
              </div>
            ) : (
              visibleNotifications.map((item) => {
                return (
                  <div
                    key={item.id}
                    onClick={() => handleNotificationClick(item)}
                    className="group relative flex items-start gap-3 p-3.5 hover:bg-slate-50 transition-colors cursor-pointer bg-primary-50/15"
                  >
                    {getCategoryIcon(item.category)}
                    <div className="flex-1 min-w-0 pr-6">
                      <div className="flex items-start justify-between gap-1">
                        <p className="text-xs font-semibold text-ink group-hover:text-primary-600 transition-colors truncate">
                          {item.title}
                        </p>
                      </div>
                      <p className="text-xs text-ink-secondary mt-0.5 line-clamp-2">{item.description}</p>
                      <div className="flex items-center gap-1 text-[11px] text-ink-muted mt-1.5">
                        <Clock className="h-3 w-3" />
                        <span>{relativeTime(item.timestamp)}</span>
                      </div>
                    </div>
                    {/* Individual dismiss button */}
                    <button
                      type="button"
                      onClick={(e) => handleDismissOne(e, item.id)}
                      title="Dismiss notification"
                      className="absolute top-3 right-3 flex h-6 w-6 items-center justify-center rounded-md text-ink-muted opacity-60 hover:opacity-100 hover:bg-slate-200 hover:text-ink transition-all cursor-pointer"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {adminBadge && (
            <div className="border-t border-line bg-slate-50 p-2.5 text-center">
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  navigate("/admin/logs");
                }}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
              >
                View full audit logs
                <ExternalLink className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
